"use client";

import { useMemo, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { useLocale } from "next-intl";
import MarketingFaq from "./faq";
import { pricingFaq, pricingTiers } from "@/content/clawagora-marketing";
import { Pricing as PricingType, PricingItem } from "@/types/blocks/pricing";

export default function MarketingPricingPage({
  pricing,
}: {
  pricing: PricingType;
}) {
  const locale = useLocale();
  const [group, setGroup] = useState<"monthly" | "yearly">("monthly");
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);

  const currentItems = useMemo(
    () => pricing.items?.filter((item) => item.group === group) ?? [],
    [group, pricing.items]
  );
  const starterItem = currentItems.find((item) =>
    item.product_id.toLowerCase().includes("starter")
  );
  const proItem = currentItems.find((item) =>
    item.product_id.toLowerCase().includes("pro")
  );

  const displayItems = pricingTiers
    .map((tier) => ({
      tier,
      realItem: tier.mapsTo === "starter" ? starterItem : proItem,
    }))
    .filter((item): item is { tier: (typeof pricingTiers)[number]; realItem: PricingItem } =>
      Boolean(item.realItem)
    );

  const handleCheckout = async (item: PricingItem) => {
    setLoadingProductId(item.product_id);

    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: item.product_id,
          currency: item.currency || "USD",
          locale: locale || "en",
        }),
      });

      if (response.status === 401) {
        window.location.href = `/auth/signin?callbackUrl=${encodeURIComponent("/pricing")}`;
        return;
      }

      const payload = await response.json();
      if (payload?.code !== 0 || !payload?.data?.checkout_url) {
        throw new Error(payload?.message || "checkout failed");
      }

      window.location.href = payload.data.checkout_url;
    } catch (error) {
      console.error(error);
      window.alert("Checkout failed. Please try again.");
    } finally {
      setLoadingProductId(null);
    }
  };

  return (
    <main id="main-content" className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <div className="text-center">
        <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
          Choose your supporter plan
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Pick the Death Stranding 2 Hub plan that matches how closely you want
          to follow launch notes, checklists, and release-week updates.
        </p>
      </div>

      <div className="mt-12 space-y-8">
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setGroup("monthly")}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              group === "monthly"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setGroup("yearly")}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              group === "yearly"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Annual
            <span className="ml-1 text-xs opacity-75">Save ~17%</span>
          </button>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {displayItems.map(({ tier, realItem }) => {
            const isLoading = loadingProductId === realItem.product_id;
            const price = group === "yearly" ? tier.annualPrice : tier.monthlyPrice;

            return (
              <div
                key={`${tier.slug}-${group}`}
                className={`relative flex flex-col rounded-2xl border p-6 ${
                  tier.badge ? "border-primary shadow-lg shadow-primary/10" : "border-border/50"
                }`}
              >
                {tier.badge ? (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                    {tier.badge}
                  </div>
                ) : null}
                <h3 className="text-xl font-bold text-foreground">{tier.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{tier.description}</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-foreground">{price}</span>
                  <span className="text-muted-foreground">/mo</span>
                </div>
                <ul className="mt-6 flex-1 space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-foreground">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 space-y-2">
                  <button
                    type="button"
                    onClick={() => handleCheckout(realItem)}
                    disabled={isLoading}
                    className={`inline-flex h-10 w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      tier.badge
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "border border-border bg-background text-foreground hover:bg-muted"
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Subscribe
                      </>
                    ) : (
                      "Subscribe"
                    )}
                  </button>
                  <p className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                    14-day money-back guarantee
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <section className="mt-24">
          <MarketingFaq items={pricingFaq} />
        </section>
      </div>
    </main>
  );
}
